import React, { useState, useEffect, useCallback } from 'react';
import {
  Cpu, RefreshCw, AlertTriangle, Save, X, Pencil, Radio, History, ChevronDown, ShieldAlert,
} from 'lucide-react';
import { Button } from './ui/button';
import { jemAPI } from '../utils/api';
import { toast } from 'sonner';
import JemPayloadModal from './JemPayloadModal';

const ago = (iso) => {
  if (!iso) return 'never';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 90) return `${Math.round(secs)}s ago`;
  if (secs < 5400) return `${Math.round(secs / 60)}m ago`;
  if (secs < 172800) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
};

export default function JemDeviceMirrorPanel({ siteId, isAdmin = false, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ station_id: '', system_id: '' });
  const [advanced, setAdvanced] = useState(false);
  const [override, setOverride] = useState({ device_id: '', field_code: '' });
  const [days, setDays] = useState(20);
  const [job, setJob] = useState(null);
  const [payloadDevice, setPayloadDevice] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const r = await jemAPI.getDevices(siteId);
      setData(r.data);
      setForm({ station_id: r.data.station_id || '', system_id: r.data.ems_id || '' });
      setOverride({
        device_id: r.data.peripheral_device_id || '',
        field_code: r.data.di_cross_check_field || '',
      });
      setNotFound(!r.data.configured && !r.data.station_id);
      try {
        const j = await jemAPI.gridBackfillStatus(siteId);
        setJob(j.data);
      } catch { /* no job yet */ }
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else toast.error(err.response?.data?.detail || 'Failed to load JEM devices');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const r = await fn();
      toast.success(typeof okMsg === 'function' ? okMsg(r.data) : okMsg);
      await fetchAll();
      onSaved?.(r.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Request failed'));
    } finally {
      setBusy('');
    }
  };

  const saveIds = () => run('ids', () => jemAPI.updatePeripheralConfig(siteId, {
    station_id: String(form.station_id).trim(),
    system_id: String(form.system_id).trim(),
  }), 'Station and EMS saved').then(() => setEditing(false));

  const saveOverride = () => run('override', () => jemAPI.setGridSource(siteId, {
    mode: 'peripheral_di',
    device_id: Number(override.device_id),
    field_code: String(override.field_code).trim(),
  }), 'Grid source override saved');

  if (loading) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-[12px] text-slate-500"
           data-testid="jem-mirror-loading">Loading JEM devices…</div>
    );
  }

  if (notFound) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4" data-testid="jem-mirror-missing">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-[12px] text-amber-900">
            <div className="font-semibold mb-0.5">No JEM mapping for this site</div>
            Link a JEM station in the JEM Portal tab first — then this panel discovers the devices for you.
          </div>
        </div>
      </div>
    );
  }

  const devices = data?.devices || [];
  const gridSource = data?.grid_source || 'unset';
  const onPcs = gridSource === 'pcs';

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="jem-mirror-panel">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Cpu className="w-4 h-4 text-slate-700" />
          <h3 className="text-sm font-semibold text-slate-900">JEM device mirror</h3>
          <span
            className={'text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border '
              + (devices.length
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-rose-50 text-rose-700 border-rose-200')}
            data-testid="jem-mirror-device-count"
          >
            {devices.length ? `${devices.length} devices discovered` : 'No devices yet'}
          </span>
          <span
            className={'text-[10px] font-semibold px-1.5 py-0.5 rounded border '
              + (onPcs ? 'bg-blue-50 text-blue-700 border-blue-200'
                : 'bg-slate-50 text-slate-600 border-slate-200')}
            data-testid="jem-mirror-grid-source"
          >
            Grid source: {onPcs ? 'PCS gridStatus' : gridSource === 'peripheral_di' ? 'Peripheral DI (override)' : 'not set'}
          </span>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={!!busy}
                    onClick={() => run('sync', () => jemAPI.syncDevices(siteId),
                      (d) => `${d.device_count} devices synced`)}
                    data-testid="jem-mirror-sync-btn">
              <RefreshCw className={'w-3 h-3 mr-1 ' + (busy === 'sync' ? 'animate-spin' : '')} /> Sync devices
            </Button>
            <Button size="sm" variant="outline" disabled={!!busy}
                    onClick={() => run('mirror', () => jemAPI.mirrorNow(siteId),
                      (d) => `Mirrored ${d.devices_polled} devices`)}
                    data-testid="jem-mirror-now-btn">
              <Radio className="w-3 h-3 mr-1" /> Mirror now
            </Button>
          </div>
        )}
      </div>

      {/* Station + EMS — the only two fields onboarding needs */}
      <div className="px-5 py-3 border-b border-slate-100 flex items-end gap-6 flex-wrap"
           data-testid="jem-mirror-ids">
        {['station_id', 'system_id'].map((k) => (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
              {k === 'station_id' ? 'Station ID' : 'EMS ID'}
            </div>
            {editing ? (
              <input
                value={form[k] ?? ''}
                onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))}
                className="w-32 text-sm border border-slate-300 rounded-sm px-2 py-1 font-mono"
                data-testid={`jem-mirror-input-${k}`}
              />
            ) : (
              <div className="text-sm font-mono text-slate-900" data-testid={`jem-mirror-value-${k}`}>
                {(k === 'station_id' ? data?.station_id : data?.ems_id) || '—'}
              </div>
            )}
          </div>
        ))}
        {isAdmin && !editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}
                  data-testid="jem-mirror-edit-ids-btn">
            <Pencil className="w-3 h-3 mr-1" /> Edit
          </Button>
        )}
        {isAdmin && editing && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); fetchAll(); }}
                    data-testid="jem-mirror-cancel-ids-btn"><X className="w-3 h-3 mr-1" /> Cancel</Button>
            <Button size="sm" onClick={saveIds} disabled={busy === 'ids'}
                    data-testid="jem-mirror-save-ids-btn"><Save className="w-3 h-3 mr-1" /> Save</Button>
          </div>
        )}
        <div className="text-[11px] text-slate-500 ml-auto max-w-sm">
          Device IDs and field codes are discovered from JEM — nothing else to type.
        </div>
      </div>

      {/* Discovered devices */}
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="jem-mirror-device-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="text-left font-medium px-5 py-2">Device</th>
              <th className="text-left font-medium px-3 py-2">Vendor name</th>
              <th className="text-left font-medium px-3 py-2">ID</th>
              <th className="text-left font-medium px-3 py-2">Comms</th>
              <th className="text-left font-medium px-3 py-2">Last payload</th>
              <th className="text-right font-medium px-3 py-2">Changes stored</th>
              <th className="text-left font-medium px-3 py-2">Every</th>
              <th className="text-right font-medium px-5 py-2">Payload</th>
            </tr>
          </thead>
          <tbody>
            {devices.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-6 text-center text-slate-500"
                      data-testid="jem-mirror-empty">
                No devices yet — click “Sync devices”.
              </td></tr>
            )}
            {devices.map((d) => (
              <tr key={d.device_id} className="border-b border-slate-100"
                  data-testid={`jem-mirror-device-row-${d.device_id}`}>
                <td className="px-5 py-2 font-medium text-slate-900">
                  {d.device_label}
                  {!d.present && <span className="ml-2 text-[10px] text-rose-600">absent</span>}
                  {!d.supported && <span className="ml-2 text-[10px] text-amber-600">no endpoint mapped</span>}
                </td>
                <td className="px-3 py-2 text-slate-600">{d.vendor_device_name || '—'}</td>
                <td className="px-3 py-2 font-mono text-slate-700">{d.device_id}</td>
                <td className="px-3 py-2">
                  <span className={d.comm_status === 1 ? 'text-emerald-700' : 'text-rose-700'}>
                    {d.comm_status === 1 ? 'online' : 'offline'}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {ago(d.state?.last_polled_at)}
                  {d.state?.last_error && (
                    <span className="block text-[10px] text-rose-600">{d.state.last_error}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono text-slate-700">{d.samples_stored}</td>
                <td className="px-3 py-2 text-slate-500">{Math.round((d.cadence_seconds || 900) / 60)} min</td>
                <td className="px-5 py-2 text-right">
                  <button type="button" onClick={() => setPayloadDevice(d)}
                          className="text-[11px] text-blue-700 hover:underline"
                          data-testid={`jem-mirror-view-payload-${d.device_id}`}>
                    View {d.state ? 'payload' : '—'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Grid history backfill */}
      {isAdmin && (
        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-3 flex-wrap"
             data-testid="jem-mirror-backfill">
          <History className="w-4 h-4 text-slate-600" />
          <span className="text-[12px] text-slate-700">Backfill grid history from PCS gridStatus</span>
          <input type="number" min={1} max={90} value={days}
                 onChange={(e) => setDays(Number(e.target.value))}
                 className="w-16 text-sm border border-slate-300 rounded-sm px-2 py-1 font-mono"
                 data-testid="jem-mirror-backfill-days" />
          <span className="text-[12px] text-slate-500">days</span>
          <Button size="sm" variant="outline" disabled={!!busy}
                  onClick={() => run('backfill', () => jemAPI.startGridBackfill(siteId, days),
                    'Backfill started — refresh in a minute')}
                  data-testid="jem-mirror-backfill-btn">Run backfill</Button>
          {job && job.status !== 'never_run' && (
            <span className="text-[11px] text-slate-500 font-mono" data-testid="jem-mirror-backfill-status">
              {job.status} · {job.samples_inserted ?? 0} new samples · {job.pcs_samples_stored ?? 0} stored
              {job.empty_chunks ? ` · ${job.empty_chunks} windows had no vendor history` : ''}
            </span>
          )}
        </div>
      )}

      {data?.conflict_count > 0 && (
        <div className="px-5 py-3 border-t border-amber-200 bg-amber-50 text-[12px] text-amber-900 flex items-start gap-2"
             data-testid="jem-mirror-conflicts">
          <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>{data.conflict_count} grid-source disagreements</strong> between the PCS
            gridStatus and the legacy DI bit ({data.di_cross_check_field}). PCS stays authoritative;
            the DI channel is kept only as a cross-check.
          </div>
        </div>
      )}

      {/* Advanced override */}
      {isAdmin && (
        <div className="border-t border-slate-200" data-testid="jem-mirror-advanced">
          <button type="button" onClick={() => setAdvanced((v) => !v)}
                  className="w-full px-5 py-2 flex items-center gap-2 text-[11px] text-slate-500 hover:text-slate-800"
                  data-testid="jem-mirror-advanced-toggle">
            <ChevronDown className={'w-3 h-3 transition-transform ' + (advanced ? 'rotate-180' : '')} />
            Advanced override — pin grid sensing to a peripheral DI channel
          </button>
          {advanced && (
            <div className="px-5 pb-4 flex items-end gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Device</div>
                <select
                  value={override.device_id}
                  onChange={(e) => setOverride((s) => ({ ...s, device_id: e.target.value }))}
                  className="text-sm border border-slate-300 rounded-sm px-2 py-1"
                  data-testid="jem-mirror-override-device"
                >
                  <option value="">Select device…</option>
                  {devices.map((d) => (
                    <option key={d.device_id} value={d.device_id}>
                      {d.device_label} · {d.device_id}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Field code</div>
                <input value={override.field_code}
                       onChange={(e) => setOverride((s) => ({ ...s, field_code: e.target.value }))}
                       placeholder="DI_bit4"
                       className="w-32 text-sm border border-slate-300 rounded-sm px-2 py-1 font-mono"
                       data-testid="jem-mirror-override-field" />
              </div>
              <Button size="sm" onClick={saveOverride} disabled={busy === 'override'}
                      data-testid="jem-mirror-override-save">Use this channel</Button>
              <Button size="sm" variant="outline" disabled={!!busy}
                      onClick={() => run('pcs', () => jemAPI.setGridSource(siteId, { mode: 'pcs' }),
                        'Grid source set back to PCS gridStatus')}
                      data-testid="jem-mirror-override-reset">Back to PCS gridStatus</Button>
              <div className="text-[11px] text-slate-500 w-full">
                Field codes are validated against the vendor catalog on save — an invalid code is
                rejected here instead of silently returning no samples.
              </div>
            </div>
          )}
        </div>
      )}

      {payloadDevice && (
        <JemPayloadModal siteId={siteId} device={payloadDevice}
                         onClose={() => setPayloadDevice(null)} />
      )}
    </div>
  );
}
